import { useState } from 'react'
import { motion } from 'framer-motion'
import { Drawer } from 'vaul'
import { MapPin, Phone, Mail, X } from 'lucide-react'
import type { JamaicaCoach } from '@/types/api'
import { cn } from '@/lib/utils'

/**
 * Parish coordinates on the SVG viewBox 0 0 300 150. Approximate positions of the
 * real Jamaican parishes we have coaches in.
 */
const parishPositions: Record<string, { x: number; y: number }> = {
  Kingston: { x: 195, y: 105 },
  'St. Ann': { x: 155, y: 55 },
  'St. Catherine': { x: 175, y: 85 },
  'St. Mary': { x: 185, y: 65 },
}

/** Simplified Jamaica outline — hand-authored SVG path (not to scale). */
const JAMAICA_PATH =
  'M 15,88 Q 25,72 45,68 Q 60,60 80,60 Q 100,52 125,55 Q 145,50 165,55 Q 185,52 210,58 Q 235,60 258,65 Q 275,68 285,80 Q 288,92 275,100 Q 260,110 235,110 Q 210,115 185,110 Q 160,115 135,110 Q 110,115 85,108 Q 60,110 40,102 Q 22,98 15,88 Z'

export function JamaicaMap({ coaches }: { coaches: JamaicaCoach[] }) {
  const [active, setActive] = useState<JamaicaCoach | null>(null)

  // Group coaches by parish so overlapping pins offset nicely
  const grouped = coaches.reduce<Record<string, JamaicaCoach[]>>((acc, c) => {
    ;(acc[c.parish] ??= []).push(c)
    return acc
  }, {})

  return (
    <div className="grid gap-8 lg:grid-cols-[1.1fr_1.4fr]">
      {/* MAP */}
      <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-pitch-950 p-6">
        <svg
          viewBox="0 0 300 150"
          className="w-full h-auto"
          role="img"
          aria-label="Cosmos F.C. Jamaica regional coach map"
        >
          <defs>
            <linearGradient id="jamaica-fill" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#1CA84A" />
              <stop offset="100%" stopColor="#146F32" />
            </linearGradient>
            <filter id="jamaica-shadow">
              <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
              <feOffset dx="0" dy="3" result="offsetblur" />
              <feFlood floodColor="#F5C518" floodOpacity="0.35" />
              <feComposite in2="offsetblur" operator="in" />
              <feMerge>
                <feMergeNode />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <motion.path
            d={JAMAICA_PATH}
            fill="url(#jamaica-fill)"
            stroke="#F5C518"
            strokeWidth="1.2"
            filter="url(#jamaica-shadow)"
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 1 }}
            viewport={{ once: true, margin: '-15% 0px' }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          />

          {/* Parish labels */}
          {Object.entries(parishPositions).map(([parish, pos]) => (
            <g key={parish}>
              <text
                x={pos.x}
                y={pos.y - 8}
                fontFamily="Anton, Impact, sans-serif"
                fontSize="6.5"
                fill="#F5C518"
                textAnchor="middle"
                letterSpacing="0.5"
              >
                {parish.replace('St. ', 'St.').toUpperCase()}
              </text>
            </g>
          ))}

          {/* Coach pins */}
          {coaches.map((c, i) => {
            const base = parishPositions[c.parish] ?? { x: 150, y: 75 }
            const sameParishIdx = (grouped[c.parish] ?? []).indexOf(c)
            const offsetX = sameParishIdx * 8 - ((grouped[c.parish] ?? []).length - 1) * 4
            return (
              <motion.g
                key={c.name}
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 + i * 0.12, type: 'spring', stiffness: 220 }}
                onClick={() => setActive(c)}
                className="cursor-pointer"
                whileHover={{ scale: 1.35 }}
              >
                <circle
                  cx={base.x + offsetX}
                  cy={base.y}
                  r="4"
                  fill="#F5C518"
                  stroke="#ffffff"
                  strokeWidth="1"
                />
                <circle
                  cx={base.x + offsetX}
                  cy={base.y}
                  r="8"
                  fill="none"
                  stroke="#F5C518"
                  strokeWidth="0.6"
                  opacity="0.5"
                >
                  <animate attributeName="r" from="4" to="12" dur="2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" from="0.6" to="0" dur="2s" repeatCount="indefinite" />
                </circle>
              </motion.g>
            )
          })}
        </svg>
        <div className="absolute bottom-4 left-6 right-6 flex items-center justify-between text-white/70 text-[10px] uppercase tracking-widest">
          <span>Jamaica · Development Recreation Program</span>
          <span className="text-cosmos-gold">{coaches.length} Regional Coaches</span>
        </div>
      </div>

      {/* COACH CARDS */}
      <div className="grid gap-3 sm:grid-cols-2">
        {coaches.map((c, i) => (
          <motion.button
            key={c.name}
            onClick={() => setActive(c)}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.06 }}
            className={cn(
              'text-left rounded-xl border border-white/15 bg-white/6 p-4 hover:bg-white/12 hover:border-cosmos-gold/60 transition-all',
              'flex gap-3 items-start group'
            )}
          >
            <div className="grid h-11 w-11 place-items-center rounded-full bg-cosmos-gold text-pitch-950 shrink-0 group-hover:scale-110 transition-transform">
              <MapPin className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display uppercase text-sm tracking-wide text-white leading-tight">
                {c.name}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-cosmos-gold mt-1">
                {c.parish}, Jamaica
              </div>
              <div className="mt-2 text-xs text-white/70 flex items-center gap-2">
                <Phone className="h-3 w-3" />
                {c.phone}
              </div>
              {c.email && (
                <div className="mt-1 text-xs text-white/60 flex items-center gap-2 truncate">
                  <Mail className="h-3 w-3 shrink-0" />
                  <span className="truncate">{c.email}</span>
                </div>
              )}
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-cosmos-gold opacity-0 group-hover:opacity-100 transition-opacity">
              View →
            </span>
          </motion.button>
        ))}
      </div>

      {/* DRAWER */}
      <Drawer.Root open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 z-50 bg-pitch-950/70 backdrop-blur-sm" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 mx-auto max-w-md rounded-t-2xl bg-card border border-border p-6">
            <div className="mx-auto h-1 w-12 rounded-full bg-border mb-4" />
            {active && (
              <div>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-xs text-cosmos-green uppercase tracking-widest font-bold">
                      {active.parish}, Jamaica
                    </div>
                    <h4 className="font-display text-2xl uppercase mt-1">{active.name}</h4>
                  </div>
                  <button
                    onClick={() => setActive(null)}
                    className="grid h-8 w-8 place-items-center rounded-full border border-border hover:bg-muted"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-4 space-y-2 text-sm">
                  <a
                    href={`tel:${active.phone}`}
                    className="flex items-center gap-2 text-foreground hover:text-cosmos-green"
                  >
                    <Phone className="h-4 w-4" /> {active.phone}
                  </a>
                  {active.email && (
                    <a
                      href={`mailto:${active.email}`}
                      className="flex items-center gap-2 text-foreground hover:text-cosmos-green break-all"
                    >
                      <Mail className="h-4 w-4 shrink-0" /> {active.email}
                    </a>
                  )}
                </div>
                <div className="mt-6 rounded-lg bg-muted/40 p-4 text-xs text-muted-foreground leading-relaxed">
                  Contact {active.name.split(' ')[0]} to enroll your player in the Cosmos F.C. Jamaica Development Recreation Football Program in {active.parish}. Ages 6-12, boys and girls welcome.
                </div>
              </div>
            )}
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  )
}
